import { useEffect, useId, useRef, useState } from 'react';
import { SubscriptionEntitlements } from '../../redux/slices/subscriptionSlice';
import styled from '@emotion/styled';

type FeatureKey = 'canApplyToJobs' | 'canPostJobs' | 'canReceiveJobAlerts' | 'canReceiveJobInvites' | 'canUseFindLocum';

interface FeatureGateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: () => void;
  feature: FeatureKey;
  featureLabel: string;
  entitlements: SubscriptionEntitlements | null;
  message?: string;
  checkFailed?: boolean;
  onRetry?: () => Promise<void>;
}

const featureMessages: Record<FeatureKey, { title: string; description: string }> = {
  canApplyToJobs: {
    title: 'Apply to Jobs',
    description: 'Your current plan limits the number of job applications you can submit each month.',
  },
  canPostJobs: {
    title: 'Post Jobs',
    description: 'Your current plan limits the number of jobs you can post each month.',
  },
  canReceiveJobAlerts: {
    title: 'Job Alerts',
    description: 'Get notified about new job opportunities that match your preferences.',
  },
  canReceiveJobInvites: {
    title: 'Job Invites',
    description: 'Receive direct invitations from employers to apply for their positions.',
  },
  canUseFindLocum: {
    title: 'Find a Locum',
    description: 'Search and invite qualified healthcare professionals to apply for your positions.',
  },
};

const FeatureGateModal = ({
  isOpen,
  onClose,
  onUpgrade,
  feature,
  featureLabel,
  entitlements,
  message: customMessage,
  checkFailed,
  onRetry,
}: FeatureGateModalProps) => {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const [checking, setChecking] = useState(false);
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); onClose(); }
      if (event.key !== 'Tab') return;
      const buttons = dialogRef.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)');
      if (!buttons?.length) return;
      const first = buttons[0], last = buttons[buttons.length - 1];
      if (!dialogRef.current?.contains(document.activeElement)) {
        event.preventDefault(); (event.shiftKey ? last : first).focus();
      }
      else if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', keydown);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener('keydown', keydown);
      if (previous?.isConnected) previous.focus();
    };
  }, [isOpen, onClose]);
  if (!isOpen) return null;

  const message = featureMessages[feature] || {
    title: featureLabel,
    description: 'This feature requires a subscription upgrade.',
  };

  return (
    <Overlay onClick={onClose}>
      <Modal ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} onClick={(e) => e.stopPropagation()}>
        <Header>
          <Icon>🔒</Icon>
          <div>
            <Title id={titleId}>{checkFailed ? 'Unable to verify access' : message.title}</Title>
            <Subtitle>{checkFailed ? 'Please retry your subscription check' : 'Your current plan allowance applies'}</Subtitle>
          </div>
        </Header>

        <Description>{checkFailed ? 'We could not confirm your plan. No action was submitted. Please try again.'
          : customMessage || message.description}</Description>

        {!checkFailed && entitlements && (feature === 'canPostJobs' || feature === 'canApplyToJobs') && <LimitInfo>
          {feature === 'canApplyToJobs' && entitlements && entitlements.monthlyJobApplicationLimit !== null && (
            <>
              <Label>Current Limit:</Label>
              <Value>{entitlements!.monthlyJobApplicationLimit >= 999 ? 'Unlimited' : `${entitlements!.monthlyJobApplicationLimit}/month`}</Value>
            </>
          )}
          {feature === 'canPostJobs' && entitlements && entitlements.monthlyJobPostLimit !== null && (
            <>
              <Label>Current Limit:</Label>
              <Value>{entitlements!.monthlyJobPostLimit >= 999 ? 'Unlimited' : `${entitlements!.monthlyJobPostLimit}/month`}</Value>
            </>
          )}
        </LimitInfo>}
        {!checkFailed && entitlements?.allowanceResetsAt && (
          <Description>Monthly allowances reset {new Date(entitlements.allowanceResetsAt).toLocaleString('en-NG', { timeZone: 'Africa/Lagos' })} (WAT).</Description>
        )}

        <Actions>
          <CancelButton ref={closeRef} type="button" onClick={onClose}>Close</CancelButton>
          <UpgradeButton type="button" disabled={checking} onClick={async () => {
            if (checkFailed && onRetry) {
              setChecking(true);
              try { await onRetry(); } finally { setChecking(false); }
            } else { onUpgrade(); onClose(); }
          }}>
            {checkFailed ? checking ? 'Checking...' : 'Check again' : 'View Plans'}
          </UpgradeButton>
        </Actions>
      </Modal>
    </Overlay>
  );
};

export default FeatureGateModal;

const Overlay = styled.div<{ onClick: () => void }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 20000;
  cursor: pointer;
  animation: fadeIn 0.2s ease;
`;

const Modal = styled.div`
  background: ${({ theme }) => theme.palette.white};
  border-radius: 16px;
  padding: 2rem;
  max-width: 420px;
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  width: 100%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  animation: slideUp 0.3s ease;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const Icon = styled.span`
  font-size: 2rem;
  margin-top: 0.25rem;
`;

const Title = styled.h3`
  margin: 0 0 0.25rem;
  font-size: 1.25rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.greyGrey1};
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.palette.greyGrey3};
`;

const Description = styled.p`
  margin: 0 0 1.5rem;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.palette.greyGrey2};
  line-height: 1.5;
`;

const LimitInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: ${({ theme }) => theme.palette.greyGrey4};
  border-radius: 8px;
  margin-bottom: 1.5rem;
`;

const Label = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.greyGrey2};
`;

const Value = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.mainBlue};
`;

const Actions = styled.div`
  display: flex;
  gap: 1rem;
`;

const CancelButton = styled.button`
  flex: 1;
  border: 1px solid ${({ theme }) => theme.palette.greyGrey4};
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.greyGrey2};
  background: ${({ theme }) => theme.palette.white};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.palette.greyGrey4};
    border-color: ${({ theme }) => theme.palette.greyGrey3};
  }
`;

const UpgradeButton = styled.button`
  flex: 1;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.white};
  background: ${({ theme }) => theme.palette.mainBlue};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.9;
  }
`;
